/**
 * SaudeDaExecucao — a aba "Saúde" do Live Board.
 *
 * O log conta TUDO o que acontece; esta aba responde o que o dono quer saber no meio de uma
 * execução de horas: está indo bem? em que etapa? vale parar? Alertas no topo (críticos
 * primeiro), uma linha por etapa e as ações na mesma tela — parar, continuar de onde parou,
 * refazer uma etapa e, sob pedido, o diagnóstico por IA.
 *
 * A situação nunca é comunicada só por cor: todo ícone vem com o rótulo escrito.
 */
import { useState } from 'react';
import {
  AlertTriangle, CheckCircle, Circle, Clock, Loader, OctagonAlert, RotateCcw, Sparkles,
  Square, StepForward, History, XCircle,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Btn, ConfirmDialog } from '@/components/ui';
import { usd } from '@/lib/format';
import { useContinueExecution } from '@/api/executions';
import { useSaudeDaExecucao, useDiagnosticoDaSaude } from '@/api/saudeDaExecucao';
import { useNavigationContext } from '@/navigation';

const SITUACAO = {
  aguardando:           { rotulo: 'aguardando',           icone: Circle,        cor: () => C.muted },
  em_andamento:         { rotulo: 'em andamento',         icone: Loader,        cor: () => C.accent },
  reforco_em_andamento: { rotulo: 'reforço em andamento', icone: RotateCcw,     cor: () => C.warn },
  fechou:               { rotulo: 'fechou',               icone: CheckCircle,   cor: () => C.ok },
  reaproveitada:        { rotulo: 'herdada',              icone: History,       cor: () => C.ok },
  fechou_com_pendencia: { rotulo: 'fechou com pendência', icone: AlertTriangle, cor: () => C.warn },
  nao_entregue:         { rotulo: 'não entregue',         icone: XCircle,       cor: () => C.bad },
  interrompida:         { rotulo: 'interrompida',         icone: Square,        cor: () => C.bad },
};
const COM_EVIDENCIA = new Set(['nao_entregue', 'reforco_em_andamento']);

const RECOMENDACAO = {
  seguir:              'seguir, sabendo',
  parar_e_continuar:   'parar e continuar de onde parou',
  parar_e_avisar:      'parar e avisar o time',
  decidir:             'decidir',
  confirmar_no_design: 'confirmar no Design Studio',
};

const CLASSE = {
  entrega:      'Problema da entrega (o agente pode corrigir)',
  plataforma:   'Problema da plataforma (avise o time)',
  ambiente:     'Problema do ambiente',
  inconclusivo: 'Inconclusivo',
};
const CONFIANCA = { alta: 'alta', media: 'média', baixa: 'baixa' };

// As etapas de CONSTRUÇÃO: só elas podem voltar ao portão (`refazer_camadas`).
const ETAPAS_DE_CONSTRUCAO = new Set([
  'dados', 'testes_integracao', 'servicos', 'telas_web', 'telas_mobile', 'docs', 'infra_cloud',
]);

/** 7800 → "2 h 10 min" · 2220 → "37 min" · 40 → "menos de 1 min". */
export function duracao(segundos) {
  const s = segundos == null ? NaN : Number(segundos);   // etapa que não começou não tem tempo
  if (!Number.isFinite(s) || s < 0) return null;
  const min = Math.floor(s / 60);
  if (min < 1) return 'menos de 1 min';
  const h = Math.floor(min / 60);
  return h ? `${h.toLocaleString('pt-BR')} h ${min % 60} min` : `${min} min`;
}

function Evidencia({ itens, rotulo }) {
  const [aberta, setAberta] = useState(false);
  if (!itens?.length) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <button type="button" aria-expanded={aberta} aria-label={`${aberta ? 'Ocultar' : 'Ver'} evidência — ${rotulo}`}
        onClick={() => setAberta((v) => !v)}
        style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 6,
          color: C.accent, fontSize: 12, fontWeight: 600, padding: '6px 10px', minHeight: 32, cursor: 'pointer' }}>
        {aberta ? 'ocultar evidência' : 'ver evidência'}
      </button>
      {aberta && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: C.dim,
          fontFamily: 'monospace', lineHeight: 1.5, wordBreak: 'break-word' }}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {itens.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}

function Alerta({ alerta }) {
  const critico = alerta.severidade === 'critico';
  const cor = critico ? C.bad : C.warn;
  const Icone = critico ? OctagonAlert : AlertTriangle;
  return (
    <li data-testid="alerta-da-saude" data-severidade={alerta.severidade}
      style={{ border: `1px solid ${cor}66`, borderLeft: `4px solid ${cor}`, borderRadius: 8,
        background: `${cor}12`, padding: '10px 12px', listStyle: 'none' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Icone size={16} aria-hidden="true" style={{ color: cor, flexShrink: 0, marginTop: 2 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
            <span style={{ color: cor }}>{critico ? 'Crítico' : 'Atenção'}</span>
            {' · '}{alerta.titulo}
            {RECOMENDACAO[alerta.recomendacao] && (
              <span style={{ fontWeight: 500, color: C.dim }}> — {RECOMENDACAO[alerta.recomendacao]}</span>
            )}
          </div>
          {alerta.mensagem && (
            <div style={{ fontSize: 12.5, color: C.dim, marginTop: 3, lineHeight: 1.45 }}>{alerta.mensagem}</div>
          )}
          <Evidencia itens={alerta.evidencia} rotulo={alerta.titulo} />
        </div>
      </div>
    </li>
  );
}

function Etapa({ etapa }) {
  const sit = SITUACAO[etapa.situacao] ?? { rotulo: etapa.situacao, icone: Circle, cor: () => C.muted };
  const Icone = sit.icone;
  const cor = sit.cor();
  const tempo = duracao(etapa.duracao_s);
  const detalhes = [
    etapa.reforcos ? `reforço ${etapa.reforcos} de ${etapa.max_reforcos ?? '—'}` : null,
    etapa.fatias ? `fatia ${etapa.fatias.feitas} de ${etapa.fatias.total}` : null,
  ].filter(Boolean);
  const numero = { fontSize: 12, color: C.dim, fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap', textAlign: 'right' };
  return (
    <li data-testid={`etapa-${etapa.chave}`}
      style={{ listStyle: 'none', padding: '9px 2px', borderBottom: `1px solid ${C.borderSoft ?? C.border}` }}>
      <div style={{ display: 'grid', columnGap: 12, alignItems: 'center', minWidth: 640,
        gridTemplateColumns: '16px minmax(150px, 1.2fr) 168px 88px 104px minmax(0, 1.4fr)' }}>
        <Icone size={14} aria-hidden="true" style={{ color: cor }} />
        <strong style={{ fontSize: 13, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis' }}>{etapa.titulo || etapa.chave}</strong>
        <span data-testid={`situacao-${etapa.chave}`}
          style={{ fontSize: 12, fontWeight: 600, color: cor, whiteSpace: 'nowrap' }}>
          {sit.rotulo}
        </span>
        <span data-testid={`tempo-${etapa.chave}`} style={numero}>{tempo || '—'}</span>
        <span data-testid={`custo-${etapa.chave}`} style={numero}>
          {etapa.custo_usd != null ? usd(etapa.custo_usd) : ''}
        </span>
        <span style={{ fontSize: 12, color: C.dim, whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis' }}>{detalhes.join(' · ')}</span>
      </div>
      {etapa.mensagem && (
        <div style={{ fontSize: 12.5, color: C.dim, margin: '3px 0 0 28px', lineHeight: 1.45 }}>{etapa.mensagem}</div>
      )}
      {COM_EVIDENCIA.has(etapa.situacao) && (
        <div style={{ marginLeft: 28 }}>
          <Evidencia itens={etapa.evidencia} rotulo={etapa.titulo || etapa.chave} />
        </div>
      )}
    </li>
  );
}

function Diagnostico({ d }) {
  const linha = (rotulo, valor) => valor && (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{rotulo}</div>
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>{valor}</div>
    </div>
  );
  return (
    <section data-testid="diagnostico-da-saude" aria-label="Diagnóstico por IA"
      style={{ border: `1px solid ${C.violet}66`, borderRadius: 10, background: `${C.violet}12`, padding: '12px 14px' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Sparkles size={15} aria-hidden="true" style={{ color: C.violet }} />
        <strong style={{ fontSize: 14, color: C.text }}>{CLASSE[d.classe] ?? d.classe}</strong>
        {d.confianca && (
          <span style={{ fontSize: 12, color: C.dim }}>confiança {CONFIANCA[d.confianca] ?? d.confianca}</span>
        )}
        {d.custo_usd != null && (
          <span style={{ fontSize: 12, color: C.dim, marginLeft: 'auto' }}>custo desta consulta: {usd(d.custo_usd)}</span>
        )}
      </div>
      {linha('Explicação', d.explicacao)}
      {d.evidencias?.length > 0 && linha('Evidências', (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {/* eslint-disable-next-line react/no-array-index-key */}
          {d.evidencias.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      ))}
      {linha('Recomendação', d.recomendacao)}
      {linha('Próximo passo', d.proximo_passo)}
    </section>
  );
}

/**
 * @param {{ executionId: string, projectId?: string|null, onParar?: () => void, parando?: boolean }} props
 *   `onParar` é a parada do próprio Live Board (mesmo hook, mesma confirmação).
 */
export function SaudeDaExecucao({ executionId, projectId, onParar, parando = false }) {
  const { data, isLoading, isError } = useSaudeDaExecucao(executionId);
  const continuar = useContinueExecution();
  const diagnostico = useDiagnosticoDaSaude(executionId);
  const { navigate } = useNavigationContext();

  const [confirmar, setConfirmar] = useState(null);   // 'continuar' | 'refazer' | 'ia'
  const [etapaARefazer, setEtapaARefazer] = useState('');
  const [erro, setErro] = useState(null);
  const [parecer, setParecer] = useState(null);
  const [erroDaIa, setErroDaIa] = useState(null);

  if (isLoading) return <p role="status" style={{ fontSize: 13, color: C.dim, padding: 16 }}>Lendo a saúde da execução…</p>;
  if (isError) return <p role="alert" style={{ fontSize: 13, color: C.bad, padding: 16 }}>Não foi possível ler a saúde desta execução.</p>;
  if (!data?.disponivel) {
    return <p role="status" style={{ fontSize: 13, color: C.dim, padding: 16 }}>Ainda não há rastro para esta execução.</p>;
  }

  const resumo = data.resumo ?? {};
  const etapas = data.etapas ?? [];
  const alertas = [...(data.alertas ?? [])]
    .sort((a, b) => (a.severidade === 'critico' ? 0 : 1) - (b.severidade === 'critico' ? 0 : 1));
  const criticos = alertas.filter((a) => a.severidade === 'critico').length;
  const refaziveis = etapas.filter((e) => ETAPAS_DE_CONSTRUCAO.has(e.chave));
  const tituloDaEtapa = (chave) => etapas.find((e) => e.chave === chave)?.titulo || chave;

  const criarContinuacao = async (refazerCamadas) => {
    setConfirmar(null);
    setErro(null);
    try {
      const res = await continuar.mutateAsync({
        projectId: data.project_id ?? projectId, fromExecutionId: executionId, refazerCamadas,
      });
      const eid = res?.execution_id;
      navigate(`/execution/liveboard${eid ? `?execution=${eid}` : ''}`);
    } catch (err) {
      setErro(err?.detail || err?.message || 'Não foi possível continuar de onde esta execução parou.');
    }
  };

  const validarComIa = async () => {
    setConfirmar(null);
    setErroDaIa(null);
    try {
      setParecer(await diagnostico.mutateAsync());
    } catch (err) {
      setErroDaIa(err?.detail || err?.message || 'Não foi possível obter o diagnóstico por IA.');
    }
  };

  const tempo = duracao(resumo.duracao_s);

  return (
    <div data-testid="saude-da-execucao" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* a) cabeçalho */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: C.dim }}>
        {tempo && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            <Clock size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
            {resumo.encerrada ? 'durou' : 'há'} <strong style={{ color: C.text }}>{tempo}</strong></span>
        )}
        {resumo.custo_usd != null && (
          <span data-testid="custo-da-saude"
            style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            <strong style={{ color: C.text }}>{usd(resumo.custo_usd)}</strong>
            {resumo.teto_usd != null && <> de {usd(resumo.teto_usd)}</>}
          </span>
        )}
        <span data-testid="contagem-de-alertas" style={{ marginLeft: 'auto', fontWeight: 600,
          whiteSpace: 'nowrap',
          color: criticos ? C.bad : alertas.length ? C.warn : C.ok }}>
          {alertas.length === 0 ? 'nenhum alerta'
            : `${alertas.length.toLocaleString('pt-BR')} ${alertas.length === 1 ? 'alerta' : 'alertas'}`
              + (criticos ? ` · ${criticos.toLocaleString('pt-BR')} ${criticos === 1 ? 'crítico' : 'críticos'}` : '')}
        </span>
      </div>

      {/* d) e e) ações */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!resumo.encerrada && onParar && (
          <Btn variant="danger" size="sm" icon={Square} onClick={onParar} disabled={parando}
            aria-label="Parar esta execução">
            {parando ? 'Parando...' : 'Parar execução'}
          </Btn>
        )}
        {resumo.encerrada && (
          <>
            <Btn variant="primary" size="sm" icon={StepForward} disabled={continuar.isPending}
              onClick={() => setConfirmar('continuar')}>
              {continuar.isPending ? 'Continuando...' : 'Continuar de onde parou'}
            </Btn>
            {refaziveis.length > 0 && (
              <Btn variant="ghost" size="sm" icon={RotateCcw} disabled={continuar.isPending}
                onClick={() => { setEtapaARefazer(refaziveis[0].chave); setConfirmar('refazer'); }}>
                Refazer etapa…
              </Btn>
            )}
          </>
        )}
        <Btn variant="ghost" size="sm" icon={Sparkles} disabled={diagnostico.isPending}
          onClick={() => setConfirmar('ia')}>
          {diagnostico.isPending ? 'Validando com IA…' : 'Validar com IA'}
        </Btn>
      </div>
      {erro && <div role="alert" data-testid="continuar-erro" style={{ fontSize: 12.5, color: C.bad }}>{erro}</div>}
      {erroDaIa && <div role="alert" data-testid="diagnostico-erro" style={{ fontSize: 12.5, color: C.bad }}>{erroDaIa}</div>}
      {parecer && !diagnostico.isPending && <Diagnostico d={parecer} />}

      {/* b) alertas — críticos primeiro */}
      {alertas.length > 0 && (
        <ul aria-label="Alertas" style={{ margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {alertas.map((a, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Alerta key={`${a.codigo}-${a.etapa ?? ''}-${i}`} alerta={a} />
          ))}
        </ul>
      )}

      {/* c) etapas */}
      <div style={{ overflowX: 'auto' }}>
        <ul aria-label="Etapas da execução" style={{ margin: 0, padding: 0 }}>
          {etapas.map((e) => <Etapa key={e.chave} etapa={e} />)}
        </ul>
      </div>

      <ConfirmDialog
        open={confirmar === 'continuar'}
        title="Continuar de onde parou?"
        confirmLabel="Continuar"
        onCancel={() => setConfirmar(null)}
        onConfirm={() => criarContinuacao()}
        message={
          <>
            Vou criar uma execução nova que continua esta em vez de gerar tudo de novo. O escopo, o
            contrato aprovado e o design são <strong>os mesmos</strong>.
            {'\n\n'}
            <strong>O que já fechou fica como está</strong> — as etapas aprovadas não rodam de novo e
            não são cobradas outra vez.
            {'\n'}
            <strong>O que ficou com pendência volta ao portão</strong> — primeiro a conferência é
            repetida; o agente só é chamado se ela reprovar, e recebe a lista exata do que corrigir.
            {'\n'}
            <strong>O que não chegou a ser construído roda agora</strong>, e a verificação final é
            feita sobre o conjunto inteiro.
          </>
        }
      />

      <ConfirmDialog
        open={confirmar === 'refazer'}
        title="Refazer etapa"
        confirmLabel={`Refazer ${tituloDaEtapa(etapaARefazer)}`}
        onCancel={() => setConfirmar(null)}
        onConfirm={() => criarContinuacao([etapaARefazer])}
        message={
          <>
            <fieldset style={{ border: 'none', margin: '0 0 12px', padding: 0 }}>
              <legend style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
                Qual etapa deve ser refeita?
              </legend>
              {refaziveis.map((e) => (
                <label key={e.chave} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 32, cursor: 'pointer' }}>
                  <input type="radio" name="etapa-a-refazer" value={e.chave}
                    checked={etapaARefazer === e.chave} onChange={() => setEtapaARefazer(e.chave)} />
                  {e.titulo || e.chave}
                  <span style={{ color: C.muted, fontSize: 12 }}>— {SITUACAO[e.situacao]?.rotulo ?? e.situacao}</span>
                </label>
              ))}
            </fieldset>
            Vou criar uma execução nova a partir desta. <strong>O que já fechou é herdado</strong>;{' '}
            a etapa escolhida <strong>volta ao portão</strong> e é refeita se a conferência reprovar.
            {'\n'}
            O escopo e o contrato <strong>não mudam</strong>.
          </>
        }
      />

      <ConfirmDialog
        open={confirmar === 'ia'}
        title="Validar com IA?"
        confirmLabel="Validar com IA"
        onCancel={() => setConfirmar(null)}
        onConfirm={validarComIa}
        message={
          'Isto usa IA para ler o andamento desta execução e dizer se o problema é da entrega, '
          + 'da plataforma ou do ambiente. Tem custo (em US$) e pode levar até um minuto. '
          + 'Não altera nada na execução.'
        }
      />
    </div>
  );
}
